import React from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { useCart } from '../../cart/contexts/CartContext'
import CouponSystem from '../../../shared/components/CouponSystem'
import { AppliedCoupon } from '../../../shared/utils/couponHelpers'
import { formatPrice } from '../../../shared/utils/checkoutHelpers'
import { buildImageUrl } from '../../../config'
import { getEffectiveUnitPrice } from '../../../constants/checkout'
import { getItemWillReturnScrap } from '../hooks/useCheckoutCalculations'

const CSS_HANDLES = [
  'orderSummary',
  'summaryHeader',
  'summaryItems',
  'summaryItem',
  'itemImage',
  'itemDetails',
  'itemName',
  'itemPrice',
  'summaryTotals',
  'summaryRow',
  'summaryLabel',
  'summaryValue',
  'summaryTotal'
] as const

interface OrderSummaryProps {
  currentStep: number
  appliedCoupons: AppliedCoupon[]
  onApplyCoupon: (code: string) => Promise<{ success: boolean; message: string }>
  onRemoveCoupon: (id: string) => void
  calculateSubtotal: () => number
  calculateShipping: () => number
  calculateDiscount: () => number
  calculateTotal: () => number
  shippingAddressZipCode: string
  isLoadingShipping: boolean
  imageErrors: Record<string, boolean>
  onImageError: (itemId: string) => void
  deliveryMethod?: 'shipping' | 'pickup' | null
  isCampinas: boolean
  scrapPreference: boolean | null
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  currentStep,
  appliedCoupons,
  onApplyCoupon,
  onRemoveCoupon,
  calculateSubtotal,
  calculateShipping,
  calculateDiscount,
  calculateTotal,
  shippingAddressZipCode,
  isLoadingShipping,
  imageErrors,
  onImageError,
  deliveryMethod,
  isCampinas,
  scrapPreference
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const { state: cartState } = useCart()

  const baseSubtotal = cartState.items.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  )
  const scrapSurchargeTotal = calculateSubtotal() - baseSubtotal

  return (
    <div className={`${handles.orderSummary} lg:col-span-1`}>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className={`${handles.summaryHeader} text-lg font-semibold text-gray-900 mb-4`}>
          Resumo do Pedido
        </h3>

        {/* Itens */}
        <div className={`${handles.summaryItems} mb-6`}>
          {cartState.items.map((item, index) => {
            const allowsScrap = (item.product as { allowsScrapReturn?: boolean }).allowsScrapReturn === true
            const willReturn = getItemWillReturnScrap(item, scrapPreference)
            const itemPrice = allowsScrap
              ? getEffectiveUnitPrice(
                  item.product.price,
                  item.product.weight ?? 0,
                  isCampinas,
                  willReturn
                )
              : item.product.price
            const scrapLabel = allowsScrap
              ? (willReturn ? 'Com devolução da sucata' : 'Sem devolução da sucata')
              : null

            return (
              <div key={index} className={`${handles.summaryItem} flex space-x-3 mb-4`}>
                <div className={`${handles.itemImage} w-16 h-16 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0`}>
                  {!imageErrors[item.product.id] && item.product.images && item.product.images.length > 0 ? (
                    <img
                      src={
                        item.product.images[0]?.startsWith('http')
                          ? item.product.images[0]
                          : buildImageUrl(item.product.images[0])!
                      }
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                      onError={() => onImageError(item.product.id)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                      <svg
                        className="w-8 h-8 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className={`${handles.itemDetails} flex-1`}>
                  <h4 className={`${handles.itemName} text-sm font-medium text-gray-900`}>
                    {item.product.name}
                  </h4>
                  {scrapLabel && (
                    <p className="text-xs text-gray-500 mt-0.5" aria-label={scrapLabel}>
                      {scrapLabel}
                    </p>
                  )}
                  <p className={`${handles.itemPrice} text-sm text-gray-600 mt-0.5`}>
                    {formatPrice(itemPrice)} x {item.quantity}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Cupons - Apenas na etapa 1 */}
        {currentStep === 1 && (
          <CouponSystem
            appliedCoupons={appliedCoupons}
            onApplyCoupon={onApplyCoupon}
            onRemoveCoupon={onRemoveCoupon}
            subtotal={calculateSubtotal()}
            className="mb-6"
          />
        )}

        {/* Totais */}
        <div className={`${handles.summaryTotals} space-y-2`}>
          {scrapSurchargeTotal > 0 && (
            <div className={`${handles.summaryRow} flex justify-between text-sm text-amber-700`}>
              <span className={`${handles.summaryLabel}`}>Acréscimo (sem sucata):</span>
              <span className={`${handles.summaryValue}`}>{formatPrice(scrapSurchargeTotal)}</span>
            </div>
          )}
          <div className={`${handles.summaryRow} flex justify-between text-sm`}>
            <span className={`${handles.summaryLabel} text-gray-600`}>Subtotal:</span>
            <span className={`${handles.summaryValue}`}>{formatPrice(calculateSubtotal())}</span>
          </div>

          {deliveryMethod !== 'pickup' && (
            <>
              {shippingAddressZipCode.replace(/\D/g, '').length === 8 ? (
                <div className={`${handles.summaryRow} flex justify-between text-sm`}>
                  <span className={`${handles.summaryLabel} text-gray-600`}>Frete:</span>
                  <span className={`${handles.summaryValue}`}>
                    {isLoadingShipping ? 'Calculando...' : formatPrice(calculateShipping())}
                  </span>
                </div>
              ) : (
                <div className={`${handles.summaryRow} flex justify-between text-sm`}>
                  <span className={`${handles.summaryLabel} text-gray-600`}>Frete:</span>
                  <span className={`${handles.summaryValue} text-gray-400`}>
                    Informe o CEP
                  </span>
                </div>
              )}
            </>
          )}

          {calculateDiscount() > 0 && (
            <div className={`${handles.summaryRow} flex justify-between text-sm text-green-600`}>
              <span className={`${handles.summaryLabel}`}>Desconto:</span>
              <span className={`${handles.summaryValue}`}>-{formatPrice(calculateDiscount())}</span>
            </div>
          )}

          <div className={`${handles.summaryRow} flex justify-between text-lg font-semibold border-t border-gray-200 pt-2`}>
            <span className={`${handles.summaryLabel} text-gray-900`}>Total:</span>
            <span className={`${handles.summaryTotal} text-blue-600`}>{formatPrice(calculateTotal())}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderSummary

